/**
 * VoiceConsentModal — consentimento LGPD de NOTA DE VOZ (story A4).
 *
 * Espelha o VISUAL do `ConsentModal` (gravação de chamada) mas com semântica
 * DIFERENTE: a nota de voz é assíncrona e o "recusar" é só cancelar (não há
 * fallback audio-only / encerrar chamada). UI PURA — o parent (o composer)
 * é quem dispara o grant (`useGrantVoiceConsent`) e passa `isGranting`/`isError`.
 *
 * Fluxo: parceiro clica o mic sem consent → abre este modal → "Aceitar" chama
 * `onAccept` (o parent faz o grant → em sucesso fecha) → a gravação é liberada.
 * "Cancelar" (`onCancel`) fecha sem gravar.
 */

import { AlertCircle, Loader2 } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

export interface VoiceConsentModalProps {
  /** Quando `false`, o modal não é renderizado. */
  open: boolean;
  /** Parceiro aceitou — o parent dispara o grant do consent. */
  onAccept: () => void;
  /** Parceiro cancelou — fecha sem gravar. */
  onCancel: () => void;
  /** Grant em andamento (trava os botões + spinner). */
  isGranting?: boolean;
  /** Grant falhou — mostra alerta + o botão vira "Tentar novamente". */
  isError?: boolean;
}

export function VoiceConsentModal({
  open,
  onAccept,
  onCancel,
  isGranting = false,
  isError = false,
}: VoiceConsentModalProps) {
  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Consentimento de nota de voz (LGPD)</AlertDialogTitle>
          <AlertDialogDescription className="space-y-2 text-left">
            <span className="block">
              Ao enviar <strong>notas de voz</strong> no atendimento, você consente que o áudio seja
              armazenado de forma segura e retido pelo prazo definido pelo setor, para fins de
              atendimento e cumprimento da LGPD.
            </span>
            <span className="block">
              O consentimento vale para as próximas notas de voz. Você pode
              <strong> revogá-lo a qualquer momento</strong> em Preferências — a revogação bloqueia
              novas gravações.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {isError && (
          <Alert variant="destructive" role="alert">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Não foi possível registrar o consentimento. Verifique sua conexão e tente novamente.
            </AlertDescription>
          </Alert>
        )}

        <AlertDialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={isGranting}>
            Cancelar
          </Button>
          <Button type="button" onClick={onAccept} disabled={isGranting} autoFocus>
            {isGranting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Registrando…
              </>
            ) : isError ? (
              "Tentar novamente"
            ) : (
              "Aceitar e gravar"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default VoiceConsentModal;
